import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  MsalAuthProvider,
  SupportLoginController,
  useMsalAuth,
  useUserStoreAuthBridge,
  useUserStoreEntitlements,
  useUserStoreContextSync,
} from '@arm/ce-shared-components';
import { UseGlobalState } from '../../../context';
import { AZUREB2CSERVICE_INIT, CLEAR_USER_DATA } from '../../../constants/actionTypes';
import { getMsalAuthConfig, getPostAuthRedirectPath } from '../../../lib/msalAuthConfig';
import {
  getEntitlementsBaseUrl,
  getEntitlementsCacheKey,
  getEntitlementsCacheTtlMs,
  getIsAIAssistanceEnabled,
  getIsCheckEntitlementsEnabled,
} from '../../../config/virtualFAEConfig';
import { clearPendingDownload } from '../../../utils/pendingDownload';
import { setAccessTokenResolver } from '../../../utils/authAccessToken';
import config from '../../../temp/config';

const SIGNOUT_EVENT = 'arm-account-signout';

const getSupportLoginOutroLink = () => `${config.REACT_APP_ORIGIN.replace(/\/$/, '')}/dashboard`;

const clearAbandonedPendingDownload = (reason) => {
  if (reason === 'dismiss' || reason === 'auth-failed') {
    clearPendingDownload();
  }
};

// Maps the MsalAuthentication user onto the shape the existing global state / global nav expect.
const mapMsalUser = (msalUser, accessToken) => ({
  name: msalUser.name,
  displayableId: msalUser.email,
  email: msalUser.email,
  idToken: msalUser.idToken,
  accessToken,
  oid: msalUser.idTokenClaims?.oid,
});

// One-way bridge: mirrors the shared MSAL auth state into the existing user context so the
// global navigation (and other UseGlobalState consumers) reflect the MSAL user in support mode,
// and routes the nav's sign-out click to the MSAL logout.
const MsalUserBridge = ({ authConfig }) => {
  const { user, isAuthenticated, isInitialized, logout, getAccessToken } = useMsalAuth(authConfig);
  const { dispatch } = UseGlobalState();

  useEffect(() => setAccessTokenResolver(getAccessToken), [getAccessToken]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (!isAuthenticated || !user) {
      dispatch({ type: CLEAR_USER_DATA });
      return;
    }

    let cancelled = false;
    const dispatchUser = (authToken) => {
      if (cancelled) {
        return;
      }

      dispatch({
        type: AZUREB2CSERVICE_INIT,
        data: { user: mapMsalUser(user, authToken), isAuth: true, isLoginDone: true },
      });
    };

    const dispatchMsalUser = async () => {
      dispatchUser(user.accessToken ?? '');

      const authToken = await getAccessToken();
      if (authToken) {
        dispatchUser(authToken);
        return;
      }

      if (!cancelled) {
        dispatch({ type: CLEAR_USER_DATA });
      }
    };

    dispatchMsalUser().catch(() => {
      if (!cancelled) {
        dispatch({ type: CLEAR_USER_DATA });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, getAccessToken, isAuthenticated, isInitialized, user]);

  useEffect(() => {
    const handleSignOut = () => {
      logout();
    };
    document.addEventListener(SIGNOUT_EVENT, handleSignOut);
    return () => document.removeEventListener(SIGNOUT_EVENT, handleSignOut);
  }, [logout]);

  return null;
};

// Sibling bridge: writes the full `user-store` payload so any consumer (e.g.
// VirtualFAE) sees accessToken + email + userContext + hasPackageEntitlements +
// timestamp regardless of which widgets are rendered on the current page.
// Lives here -- not inside VirtualFAE -- because not every support page
// renders the chat widget, but every support page must have an authoritative
// store snapshot post-login. Bails in iframes so it never competes with
// Support's own writer.
const UserStoreBridge = ({ authConfig }) => {
  const { accessToken, getAccessToken, isAuthenticated } = useMsalAuth(authConfig);
  const isCheckEntitlementsEnabled = getIsCheckEntitlementsEnabled();

  useUserStoreAuthBridge(authConfig);
  useUserStoreContextSync({
    isAIAssistanceEnabled: getIsAIAssistanceEnabled(),
    isCheckEntitlementsEnabled,
  });

  // Drive entitlement resolution from the shared CE hook. The hook owns the
  // user-store timestamp TTL check and only calls the entitlement service when
  // the persisted value is missing or stale.
  useUserStoreEntitlements(
    accessToken ?? null,
    getEntitlementsBaseUrl(),
    isCheckEntitlementsEnabled && isAuthenticated,
    {
      cacheKey: getEntitlementsCacheKey(),
      cacheTtlMs: getEntitlementsCacheTtlMs(),
    },
    undefined,
    getAccessToken
  );

  return null;
};

// Mounted only on support-tagged pages (see GlobalNavigation). Provides the MSAL/B2C login
// experience: silent SSO, the auto-opening login modal, and the user/logout bridge.
const SupportAuth = () => {
  const location = useLocation();
  const routeKey = `${location.pathname || '/'}${location.search}${location.hash}`;
  const authConfig = useMemo(() => getMsalAuthConfig(), [routeKey]);
  const postAuthRedirectPath = useMemo(() => getPostAuthRedirectPath(), [routeKey]);

  return (
    <MsalAuthProvider authConfig={authConfig}>
      <SupportLoginController
        allowCloseOnOverlayClick={false}
        onModalClose={clearAbandonedPendingDownload}
        postAuthRedirectPath={postAuthRedirectPath}
        dictionary={{
          outroLink: getSupportLoginOutroLink(),
        }}
      />
      <MsalUserBridge authConfig={authConfig} />
      <UserStoreBridge authConfig={authConfig} />
    </MsalAuthProvider>
  );
};

export default SupportAuth;
